import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';

import { Link } from 'react-router-dom';
import {
    createStyles,
    IconButton,
    makeStyles,
    Typography,
} from '@material-ui/core';

import urls from '../../app/urls';
import { InfoMessage, PageLayout } from '../../common/components';
import { getBreadCrumb } from '../../common/breadcrumbUtil';
import apiUrls from '../../youwell-common/apiUrls';
import { DeleteIcon } from '../../common/icons';
import * as ProgramStore from '../../app/store/ProgramStore';
import { Image } from '../../youwell-common/components';
import { sortByMany, toLookup } from '../../youwell-common/arrayUtils';

const useStyles = makeStyles(theme => createStyles({
    main: {
    },
    tasks: {
        paddingTop: theme.spacing(2),
        minHeight: 300,
    },
    task: {
        marginTop: theme.spacing(2),
        background: theme.youwell.colors.inputBackground,
        minHeight: theme.spacing(15),
        wordBreak: 'break-word',
    },
    moduleTitle: {
        paddingTop: theme.spacing(4),
        marginLeft: theme.spacing(20),
        fontWeight: 500,

        [theme.breakpoints.down('xs')]: {
            paddingTop: theme.spacing(2),
            marginLeft: theme.spacing(2),
        },
    },
    taskDescription: {
        marginLeft: theme.spacing(20),
        textDecoration: 'underline',

        [theme.breakpoints.down('xs')]: {
            marginLeft: theme.spacing(2),
        },
    },
    imageDisplay: {
        margin: theme.spacing(2.5),
        width: 120,
        height: 80,
        objectFit: 'contain',
        float: 'left',

        [theme.breakpoints.down('xs')]: {
            width: 60,
            height: 40,
            margin: theme.spacing(2.5, 1),
        },
    },
    deleteButton: {
        float: 'right',
    },
}));

const buildFavouriteViewModel = (favTask, elementLookup, groupedElements) => {
    let el = elementLookup[favTask.patientProgramElementId];
    if (!el) {
        return null;
    }

    const elementGroup = el.elementGroup;
    el = groupedElements[elementGroup].find(d => d.id === el.id);

    let task;
    let elementName;
    let elementSubIndex;

    if (el.task && el.task.id === favTask.taskId) {
        task = el.task;
        elementName = el.name || el.task.name;
    } else if (el.module && el.module.tasks) {
        elementSubIndex = el.module.tasks.findIndex(d => d.id === favTask.taskId);
        task = el.module.tasks[elementSubIndex];
        elementName = task?.name;
    }

    return {
        task,
        elementName,
        elementGroup,
        indexInGroup: el.indexInGroup,
        elementSubIndex,
        patientProgramElementId: el.id,
        moduleName: el.module?.name,
        moduleImageId: el.module?.moduleImageId,
    };
};

const Favourites = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const translate = useSelector(state => getTranslate(state.localize));
    const clientConfig = useSelector(state => state.auth.clientConfig);
    const currentProgram = useSelector(state => state.programStore.currentProgram);

    const deleteFavourite = favToDelete => () => {
        const data = {
            taskId: favToDelete.task.id,
            patientProgramElementId: favToDelete.patientProgramElementId,
            patientProgramId: currentProgram.id,
        };

        dispatch(ProgramStore.actionCreators.toggleFavourite(data, { reducerParams: { patientProgramId: data.patientProgramId } }));
    };

    if (!currentProgram) {
        return null;
    }

    const elementLookup = toLookup(currentProgram.elements);
    const favouriteList = sortByMany(
        currentProgram.favouriteTasks
            .map(t => buildFavouriteViewModel(t, elementLookup, currentProgram.groupedElements))
            .filter(d => d),
        'elementGroup', 'indexInGroup', 'elementSubIndex',
    ) || [];

    return (
        <PageLayout
            title={translate('favourites.header')}
            breadcrumb={getBreadCrumb(translate('favourites.favourites'), translate, clientConfig)}
        >
            <div className={classes.main}>
                {!favouriteList.length && (
                    <InfoMessage messageKey="favourites.noFavourites" />
                )}
                <div className={classes.tasks}>
                    {favouriteList.map(fav => (
                        <div key={fav.task.id} className={classes.task}>
                            <IconButton
                                className={classes.deleteButton}
                                onClick={deleteFavourite(fav)}
                            >
                                <DeleteIcon />
                            </IconButton>
                            <Link
                                to={`${urls.inside.program}/${fav.elementGroup}/${fav.indexInGroup}/${fav.elementSubIndex}`}
                            >
                                {fav.moduleImageId && (
                                    <Image
                                        alt={`${translate('favourites.module')}: ${fav.moduleName}`}
                                        src={`${apiUrls.baseUrl}/${apiUrls.media.file}/${fav.moduleImageId}`}
                                        className={classes.imageDisplay}
                                    />
                                ) }
                                {fav.moduleName && (
                                    <Typography variant="subtitle1" className={classes.moduleTitle}>
                                        {`${translate('favourites.module')}: ${fav.moduleName}`}
                                    </Typography>
                                ) }
                                <Typography variant="subtitle1" className={classes.taskDescription} color="primary">
                                    {fav.elementName}
                                </Typography>
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </PageLayout>
    );
};

export default Favourites;
